/**
 * 甘特圖拖曳 → store command 的轉換。
 *
 * 拖曳條的結果不能只寫 `start`/`finish`：自動排程會在 400ms 後重算並把任務推回
 * CPM 位置，使用者的拖曳看起來就像「彈回去」。MS Project 的語義是拖曳等於加上
 * 一個 **SNET（Start No Earlier Than）限制**，所以這裡一併寫入 constraint ——
 * 排程引擎會尊重它，位置才會留住。
 */

import {
  minutes,
  resolveCalendar,
  workingMinutesBetween,
  type Calendar,
  type ISODateTime,
  type Project,
  type Task,
} from '@bigint-project/core';

function calendarFor(project: Project, task: Task): ReturnType<typeof resolveCalendar> | null {
  const byId = new Map<string, Calendar>(project.calendars.map((cal) => [cal.id as string, cal]));
  const target =
    (task.calendarId !== null ? byId.get(task.calendarId as string) : undefined) ??
    byId.get(project.defaultCalendarId as string) ??
    project.calendars[0];
  if (target === undefined) return null;
  try {
    return resolveCalendar(target, byId);
  } catch {
    return null;
  }
}

/**
 * 由新的起訖時間算出工期（工作分鐘）。日曆不可用或計算失敗時回傳 null，
 * 呼叫端應保留原本的工期而不是寫入一個猜出來的值。
 */
export function durationBetween(project: Project, task: Task, start: ISODateTime, finish: ISODateTime): number | null {
  const calendar = calendarFor(project, task);
  if (calendar === null) return null;
  try {
    const value = workingMinutesBetween(calendar, start, finish) as number;
    return Number.isFinite(value) && value >= 0 ? value : null;
  } catch {
    return null;
  }
}

export interface TaskDatePatch {
  readonly start: ISODateTime;
  readonly finish: ISODateTime;
  readonly constraintType: Task['constraintType'];
  readonly constraintDate: ISODateTime;
  readonly durationMinutes?: Task['durationMinutes'];
}

/** 把「拖曳到新的起訖時間」轉成 `updateTask` 的 patch。 */
export function buildTaskDatePatch(
  project: Project,
  task: Task,
  newStart: ISODateTime,
  newFinish: ISODateTime,
): TaskDatePatch {
  const duration = task.isMilestone ? 0 : durationBetween(project, task, newStart, newFinish);
  const base: TaskDatePatch = {
    start: newStart,
    finish: newFinish,
    constraintType: 'SNET',
    constraintDate: newStart,
  };
  if (duration === null) return base;
  return { ...base, durationMinutes: minutes(duration) };
}
